import { useEffect, useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export type NameDialogValues = {
  name: string;
  description?: string;
};

export function createNameDialogSchema(bounds?: { nameMax?: number; descriptionMax?: number }) {
  const name =
    bounds?.nameMax != null
      ? z
          .string()
          .min(1, 'Name is required')
          .max(bounds.nameMax, `Keep the name under ${bounds.nameMax} characters`)
      : z.string().min(1, 'Name is required');

  const description =
    bounds?.descriptionMax != null
      ? z
          .string()
          .max(
            bounds.descriptionMax,
            `Keep the description under ${bounds.descriptionMax} characters`,
          )
          .optional()
      : z.string().optional();

  return z.object({ name, description });
}

/** Snapshot rename defaults: 100-char name, 1000-char description. */
export const nameDialogSchema = createNameDialogSchema({ nameMax: 100, descriptionMax: 1000 });

export interface NameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  nameLabel: string;
  namePlaceholder?: string;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  submitLabel: string;
  pendingLabel?: string;
  isPending?: boolean;
  nameMax?: number;
  descriptionMax?: number;
  /**
   * Seeded whenever the dialog opens (and when this object identity changes
   * while open). Callers must pass a stable `values` reference (e.g. useMemo).
   */
  values: NameDialogValues;
  onSubmit: (data: NameDialogValues) => void;
}

/**
 * Shared name + optional description dialog. Callers own submit side effects
 * and must close the dialog on success.
 */
export function NameDialog({
  open,
  onOpenChange,
  title,
  description,
  nameLabel,
  namePlaceholder,
  descriptionLabel = 'Description (optional)',
  descriptionPlaceholder,
  submitLabel,
  pendingLabel = 'Saving…',
  isPending = false,
  nameMax,
  descriptionMax,
  values,
  onSubmit,
}: NameDialogProps) {
  const reactId = useId();
  const nameId = `${reactId}-name`;
  const descriptionId = `${reactId}-description`;
  const schema = useMemo(
    () => createNameDialogSchema({ nameMax, descriptionMax }),
    [nameMax, descriptionMax],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NameDialogValues>({
    resolver: zodResolver(schema),
    defaultValues: values,
  });

  useEffect(() => {
    if (open) reset(values);
  }, [open, values, reset]);

  function handleOpenChange(next: boolean) {
    if (!next) reset(values);
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-1">
          <div>
            <label className="mb-1.5 block text-sm font-medium" htmlFor={nameId}>
              {nameLabel}
            </label>
            <Input id={nameId} {...register('name')} placeholder={namePlaceholder} autoFocus />
            {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium" htmlFor={descriptionId}>
              {descriptionLabel}
            </label>
            <Input
              id={descriptionId}
              {...register('description')}
              placeholder={descriptionPlaceholder}
            />
            {errors.description && (
              <p className="mt-1 text-xs text-destructive">{errors.description.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending ? pendingLabel : submitLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
